/* ORDER STATUS DATA (Max 02/11: "làm 1 cái bảng data để user có thể điền luôn"; chosen: read-only while the Excel file is
   loaded every morning, so nothing typed here is overwritten by the next file). The newest Order Status upload, every
   column of the master. Typing in the portal is the next step, once Sales stops keeping the Excel file.

   Max, same day: no pager, "cho user scroll down" — and Excel's sort + filter on every header. 54,000 rows cannot be sent
   or drawn whole, so the table is VIRTUAL: the scroll height is the full count, only the rows in view (and a margin) are
   drawn, and they are fetched from the server 200 at a time as they come into view. Sorting and the column filters run
   on the server (/api/model-color/data); a column's menu lists its values from /data/values, under the other filters. */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileSpreadsheet } from 'lucide-react';
import { SortTh } from './15_SampleTracker';
import { BACKEND_URL } from './backendConfig';
import './OrderStatusData.css';

const API = `${BACKEND_URL}/api/model-color`;
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const dmy = (s) => (s ? `${s.slice(8, 10)}/${MON[Number(s.slice(5, 7)) - 1]}/${s.slice(0, 4)}` : '');
const n0 = (v) => (v == null ? '' : Number(v).toLocaleString('en-US', { maximumFractionDigits: 2 }));
const PAGE = 200;
const ROW_H = 26;        // a row's drawn height (OrderStatusData.css); measured once drawn, in case a font makes it differ
const MARGIN = 30;       // rows drawn above and below the visible ones
/* the master's own headers, in its order */
const COLS = [
  ['row_no', 'Row', 'num', 64], ['no_text', 'No.', '', 70], ['ex_factory', 'Ex-factory', '', 96], ['delivery_from', 'Delivery from', '', 112],
  ['overseas_corp', 'Overseas Corp.', '', 128], ['po_no', 'PO No.', '', 128], ['cycle_text', 'Cycle', '', 84], ['buyer', 'Buyer', '', 170],
  ['buyer_sap', 'Buyer SAP code', '', 124], ['gubun', '구분', '', 96], ['line', 'Line', '', 90], ['douzone_item', '더존 Item No.', '', 112],
  ['sap_item', 'SAP Item No.', '', 112], ['item', 'Item', '', 260], ['model_frame', 'Model (frame)', '', 160], ['product_color', 'Product color', '', 128],
  ['frame_color', 'Frame color', '', 120], ['sd', 'S/D', 'date', 104], ['qty', "Q'ty", 'num', 80], ['amount_krw', 'Amount(₩)', 'num', 116],
  ['amount_usd', 'Amount($)', 'num', 108], ['entry_date', 'Date of entry', 'date', 116], ['po_date', 'PO date', 'date', 104], ['sd2', 'S/D2', 'date', 104],
  ['bod', 'B/OD', '', 76], ['model', 'Model', '', 160],
];
const WIDTH = COLS.reduce((s, c) => s + c[3], 0);

export default function OrderStatusData({ reloadKey }) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState({ key: 'row_no', dir: 'asc' });
  const [colFilters, setColFilters] = useState({});
  const [openFilter, setOpenFilter] = useState(null);     // { key, anchor }
  const [menuValues, setMenuValues] = useState(null);     // { key, values } for the open menu
  const [meta, setMeta] = useState(null);                 // { upload, total, qty } of the current query
  const [err, setErr] = useState('');
  const [view, setView] = useState({ top: 0, height: 600 });
  const [rowH, setRowH] = useState(ROW_H);
  const [, setTick] = useState(0);
  const wrapRef = useRef(null);
  const pages = useRef(new Map());     // page → rows, for the current query only
  const asked = useRef(new Set());     // pages on their way

  /* the search box waits for a pause in typing before it asks the server */
  useEffect(() => { const h = setTimeout(() => setQ(search.trim()), 350); return () => clearTimeout(h); }, [search]);
  const qs = useMemo(() => {
    const p = new URLSearchParams();
    if (q) p.set('q', q);
    if (Object.keys(colFilters).length) p.set('f', JSON.stringify(colFilters));
    p.set('sort', sort.key); p.set('dir', sort.dir);
    return p.toString();
  }, [q, colFilters, sort]);
  const qsRef = useRef(qs);

  const fetchPage = useCallback((page) => {
    if (pages.current.has(page) || asked.current.has(page)) return;
    const mine = qsRef.current;
    asked.current.add(page);
    fetch(`${API}/data?${mine}&page=${page}&size=${PAGE}`, { credentials: 'include' })
      .then((r) => r.json().then((d) => { if (!r.ok) throw new Error(d.error || 'Error'); return d; }))
      .then((d) => {
        if (mine !== qsRef.current) return;      // a filter changed while this page was on its way
        pages.current.set(page, d.rows);
        if (page === 0) setMeta({ upload: d.upload, total: d.total || 0, qty: d.qty || 0 });
        setTick((n) => n + 1);
      })
      .catch((e) => { if (mine === qsRef.current) setErr(e.message); })
      .finally(() => { if (mine === qsRef.current) asked.current.delete(page); });
  }, []);

  /* a new query (or a new upload) starts from the top with nothing cached */
  useEffect(() => {
    qsRef.current = qs;
    pages.current = new Map();
    asked.current = new Set();
    setErr('');
    setMeta((m) => (m ? { ...m, total: m.total } : m));
    if (wrapRef.current) wrapRef.current.scrollTop = 0;
    setView((v) => ({ ...v, top: 0 }));
    fetchPage(0);
  }, [qs, reloadKey, fetchPage]);

  const onScroll = () => {
    const el = wrapRef.current;
    if (el) setView({ top: el.scrollTop, height: el.clientHeight });
  };
  useLayoutEffect(() => { onScroll(); }, [meta]);
  useEffect(() => { window.addEventListener('resize', onScroll); return () => window.removeEventListener('resize', onScroll); }, []);

  const total = meta?.total || 0;
  const first = Math.max(0, Math.floor(view.top / rowH) - MARGIN);
  const last = Math.min(total, Math.ceil((view.top + view.height) / rowH) + MARGIN);
  useEffect(() => {
    if (!total) return;
    for (let p = Math.floor(first / PAGE); p <= Math.floor(Math.max(first, last - 1) / PAGE); p += 1) fetchPage(p);
  }, [first, last, total, fetchPage, qs]);

  /* the real row height, once a row is drawn — the scroll height and the offsets are counted in it */
  const measure = useCallback((el) => {
    if (el && el.offsetHeight && Math.abs(el.offsetHeight - rowH) > 0.5) setRowH(el.offsetHeight);
  }, [rowH]);

  /* a column's menu: its values come from the server when it opens, and the menu is drawn only once they are here */
  useEffect(() => {
    if (!openFilter) { setMenuValues(null); return undefined; }
    let live = true;
    const p = new URLSearchParams(qs);
    p.set('col', openFilter.key);
    fetch(`${API}/data/values?${p.toString()}`, { credentials: 'include' })
      .then((r) => r.json().then((d) => { if (!r.ok) throw new Error(d.error || 'Error'); return d; }))
      .then((d) => { if (live) setMenuValues({ key: openFilter.key, values: d.values }); })
      .catch((e) => { if (live) { setErr(e.message); setOpenFilter(null); } });
    return () => { live = false; };
  }, [openFilter, qs]);

  const th = (key, label, className) => ({
    label, sortKey: key, sort, className,
    onSort: (k, dir) => setSort((prev) => ({ key: k, dir: dir || (prev.key === k && prev.dir === 'asc' ? 'desc' : 'asc') })),
    filterValues: menuValues?.key === key ? menuValues.values : undefined,
    filterSelected: colFilters[key],
    filterOpen: openFilter?.key === key && menuValues?.key === key,
    filterAnchor: openFilter?.key === key ? openFilter.anchor : null,
    onOpenFilter: (k, anchor) => { setMenuValues(null); setOpenFilter({ key: k, anchor }); },
    onCloseFilter: () => setOpenFilter(null),
    onApplyFilter: (k, picked, all) => {
      setColFilters((prev) => { const next = { ...prev }; if (!picked.length || picked.length === all.length) delete next[k]; else next[k] = picked; return next; });
      setOpenFilter(null);
    },
  });
  const clear = () => { setSearch(''); setQ(''); setColFilters({}); setSort({ key: 'row_no', dir: 'asc' }); };
  const up = meta?.upload;
  const rows = [];
  for (let i = first; i < last; i += 1) rows.push({ i, r: pages.current.get(Math.floor(i / PAGE))?.[i % PAGE] });

  return (
    <div className="osd" data-osd>
      {up && (
        <p className="osd-file" data-osd-file>
          {t('xOrderStatus.fileLine', { file: up.file_name, at: `${dmy(up.at.slice(0, 10))} ${up.at.slice(11, 16)}`, by: up.source === 'feed' ? t('xOrderStatus.byFeed') : up.uploaded_by, n: Number(up.row_count).toLocaleString('en-US') })}
        </p>
      )}
      <div className="sbc-tools osd-tools">
        <label className="iv-field"><span>{t('xOrderStatus.search')}</span>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('xOrderStatus.searchPh')} data-osd-search />
        </label>
        <button type="button" className="pa-btn" onClick={clear} data-osd-clear>{t('xOrderStatus.clear')}</button>
        <a className="pa-btn" href={`${API}/data/xlsx?${qs}`} download><FileSpreadsheet size={14} /> {t('xOrderStatus.excel')}</a>
        {meta && <span className="osd-count" data-osd-count>{t('xOrderStatus.shown', { n: total.toLocaleString('en-US'), q: n0(meta.qty) })}</span>}
      </div>
      {err && <p className="sbc-err" role="alert">{err}</p>}
      {!meta && !err && <p>{t('xOrderStatus.loading')}</p>}
      {meta && !up && <p className="sbc-muted">{t('xOrderStatus.noFile')}</p>}
      {meta && up && (
        <div className="osd-wrap" ref={wrapRef} onScroll={onScroll}>
          <table className="sbc-table osd-table" style={{ width: WIDTH }}>
            <colgroup>{COLS.map(([k, , , w]) => <col key={k} style={{ width: w }} />)}</colgroup>
            <thead>
              <tr>{COLS.map(([k, label, kind]) => <SortTh key={k} {...th(k, label, kind === 'num' ? 'num' : '')} />)}</tr>
            </thead>
            <tbody>
              {first > 0 && <tr className="osd-spacer" style={{ height: first * rowH }}><td colSpan={COLS.length} /></tr>}
              {rows.map(({ i, r }, n) => (
                <tr key={i} ref={n === 0 ? measure : undefined} data-osd-row={i}>
                  {COLS.map(([k, , kind]) => {
                    if (!r) return <td key={k} className="osd-wait">{k === 'row_no' ? '…' : ''}</td>;
                    const v = r[k];
                    const shown = kind === 'date' ? dmy(v) : kind === 'num' ? n0(v) : (v ?? '');
                    return <td key={k} className={kind === 'num' ? 'num' : ''} title={kind ? undefined : (v ?? '')}>{shown}</td>;
                  })}
                </tr>
              ))}
              {last < total && <tr className="osd-spacer" style={{ height: (total - last) * rowH }}><td colSpan={COLS.length} /></tr>}
              {!total && <tr><td colSpan={COLS.length} className="sbc-muted">{t('xOrderStatus.noRows')}</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
